import { Column, Entity, Index, JoinColumn, ManyToOne } from 'typeorm';
import { BaseEntity } from './base.entity';
import { ReconSignalEntity } from './recon-signal.entity';
import { ReconSocialProfileEntity } from './recon-social-profile.entity';

/**
 * Scored opportunities ready for human review.
 * Created from signals after AI scoring and prioritization.
 */
@Entity('recon_opportunities')
@Index('idx_recon_opportunities_status', ['status', 'priority'])
@Index('idx_recon_opportunities_score', ['compositeScore'])
export class ReconOpportunityEntity extends BaseEntity {
	// Links
	@Column('int', { name: 'signal_id', unsigned: true })
	public signalId!: number;

	@Column('int', { name: 'social_profile_id', unsigned: true, nullable: true })
	public socialProfileId!: number | null;

	// Scoring
	/**
	 * Composite score from 0-100.
	 */
	@Column('int', { name: 'composite_score' })
	public compositeScore!: number;

	/**
	 * Priority derived from score.
	 * 'critical', 'high', 'medium', 'low'
	 */
	@Column('varchar', { name: 'priority', length: 20 })
	public priority!: string;

	/**
	 * Individual score components.
	 * { intentStrength, icpFit, authorAuthority, recency, engagement, reachability, competitorRisk }
	 */
	@Column('json', { name: 'score_components' })
	public scoreComponents!: Record<string, number>;

	@Column('text', { name: 'score_explanation', nullable: true })
	public scoreExplanation!: string | null;

	// Snapshots (denormalized for fast display)
	/**
	 * Snapshot of person info at scoring time.
	 * { name, handle, title, company, avatarUrl, followerCount, platformId }
	 */
	@Column('json', { name: 'person_snapshot' })
	public personSnapshot!: Record<string, unknown>;

	/**
	 * Snapshot of signal info at scoring time.
	 * { contentType, contentText, publishedAt, metrics, platformUrl }
	 */
	@Column('json', { name: 'signal_snapshot' })
	public signalSnapshot!: Record<string, unknown>;

	// State
	/**
	 * Processing status.
	 * 'new', 'in_review', 'actioned', 'dismissed', 'expired'
	 */
	@Column('varchar', { name: 'status', length: 30, default: 'new' })
	public status!: string;

	@Column('int', { name: 'assigned_to', unsigned: true, nullable: true })
	public assignedTo!: number | null;

	@Column('int', { name: 'reviewed_by', unsigned: true, nullable: true })
	public reviewedBy!: number | null;

	@Column('datetime', { name: 'reviewed_at', nullable: true })
	public reviewedAt!: Date | null;

	@Column('varchar', { name: 'dismiss_reason', length: 50, nullable: true })
	public dismissReason!: string | null;

	@Column('text', { name: 'dismiss_note', nullable: true })
	public dismissNote!: string | null;

	@Column('datetime', { name: 'snoozed_until', nullable: true })
	public snoozedUntil!: Date | null;

	// CRM linkage
	@Column('int', { name: 'client_contact_id', unsigned: true, nullable: true })
	public clientContactId!: number | null;

	// Expiration
	@Column('datetime', { name: 'expires_at', nullable: true })
	public expiresAt!: Date | null;

	// Relations
	@ManyToOne(() => ReconSignalEntity)
	@JoinColumn({ name: 'signal_id' })
	public signal?: ReconSignalEntity;

	@ManyToOne(() => ReconSocialProfileEntity)
	@JoinColumn({ name: 'social_profile_id' })
	public socialProfile?: ReconSocialProfileEntity;
}
